import React, { useState } from 'react';
import { generateCopy, abTestCopy, analyzeCopy } from '../utils/api';
import { PageTitle, Card, Btn, Input, Loading, Badge } from '../components/UI';

const s = {
  select: { background: '#0f0f17', border: '1px solid #1e1e2e', borderRadius: 8, padding: '10px 14px', color: '#e8e8f0', width: '100%', fontSize: 14 },
  label:  { display: 'block', marginBottom: 6, fontSize: 12, color: '#6b6b80', fontWeight: 600 },
  copy:   { background: '#0f0f17', border: '1px solid #1e1e2e', borderRadius: 12, padding: 20, marginBottom: 16 },
  tag:    { display: 'inline-block', padding: '3px 10px', borderRadius: 100, fontSize: 11, fontWeight: 700 },
};

const ctrColor = { HIGH: '#55efc4', MEDIUM: '#ffd200', LOW: '#ff6584' };

export default function AdCopy() {
  const [tab, setTab] = useState('generate');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Generate form
  const [form, setForm] = useState({ productName: '', productDescription: '', targetAudience: '', offer: '', tone: 'emotional', language: 'bn', variants: 5 });

  // AB test form
  const [abForm, setAbForm] = useState({ existingCopy: '', improvements: '' });

  // Analyze form
  const [analyzeForm, setAnalyzeForm] = useState({ copy: '', ctr: '', cpc: '', roas: '' });

  const handleGenerate = async () => {
    if (!form.productName || !form.productDescription) return alert('Product name & description required');
    setLoading(true); setResult(null);
    try {
      const r = await generateCopy({ ...form, variants: Number(form.variants) });
      setResult({ type: 'generate', data: r.data?.data });
    } catch (e) { alert('Error: ' + (e.response?.data?.message || e.message)); }
    setLoading(false);
  };

  const handleABTest = async () => {
    if (!abForm.existingCopy) return alert('Existing copy required');
    setLoading(true); setResult(null);
    try {
      const r = await abTestCopy({ existingCopy: abForm.existingCopy, improvements: abForm.improvements.split(',').map(s => s.trim()) });
      setResult({ type: 'abtest', data: r.data?.data });
    } catch (e) { alert('Error: ' + (e.response?.data?.message || e.message)); }
    setLoading(false);
  };

  const handleAnalyze = async () => {
    if (!analyzeForm.copy) return alert('Ad copy required');
    setLoading(true); setResult(null);
    try {
      const r = await analyzeCopy({ copy: analyzeForm.copy, metrics: { ctr: analyzeForm.ctr, cpc: analyzeForm.cpc, roas: analyzeForm.roas } });
      setResult({ type: 'analyze', data: r.data?.data });
    } catch (e) { alert('Error: ' + (e.response?.data?.message || e.message)); }
    setLoading(false);
  };

  const tabs = ['generate', 'ab-test', 'analyze'];

  return (
    <div>
      <PageTitle title="✍️ Ad Copy Generator" sub="AI দিয়ে Facebook ad copy তৈরি করো" />

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {tabs.map(t => (
          <Btn key={t} color={tab === t ? '#6c63ff' : '#1e1e2e'} onClick={() => { setTab(t); setResult(null); }}>
            {t === 'generate' ? '🪄 Generate' : t === 'ab-test' ? '🔀 A/B Test' : '🔬 Analyze'}
          </Btn>
        ))}
      </div>

      {/* Generate Tab */}
      {tab === 'generate' && (
        <Card>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Input label="Product Name *" value={form.productName} onChange={e => setForm({ ...form, productName: e.target.value })} />
            <Input label="Target Audience" value={form.targetAudience} onChange={e => setForm({ ...form, targetAudience: e.target.value })} placeholder="যেমন: ১৮-৩৫ বছরের মহিলা" />
            <div style={{ gridColumn: '1/-1' }}>
              <label style={s.label}>Product Description *</label>
              <textarea value={form.productDescription} onChange={e => setForm({ ...form, productDescription: e.target.value })}
                style={{ ...s.select, height: 80, resize: 'vertical', fontFamily: 'inherit' }} placeholder="পণ্যের বিবরণ..." />
            </div>
            <Input label="Special Offer" value={form.offer} onChange={e => setForm({ ...form, offer: e.target.value })} placeholder="যেমন: ৳200 ছাড়, Free delivery" />
            <div>
              <label style={s.label}>Tone</label>
              <select value={form.tone} onChange={e => setForm({ ...form, tone: e.target.value })} style={s.select}>
                <option value="emotional">Emotional</option>
                <option value="urgency">Urgency</option>
                <option value="informative">Informative</option>
                <option value="funny">Funny</option>
              </select>
            </div>
            <div>
              <label style={s.label}>Language</label>
              <select value={form.language} onChange={e => setForm({ ...form, language: e.target.value })} style={s.select}>
                <option value="bn">বাংলা</option>
                <option value="en">English</option>
                <option value="mixed">Banglish</option>
              </select>
            </div>
            <Input label="Variants" value={form.variants} onChange={e => setForm({ ...form, variants: e.target.value })} type="number" />
          </div>
          <Btn onClick={handleGenerate} disabled={loading}>{loading ? '⏳ Generating...' : '🪄 Generate Copies'}</Btn>
        </Card>
      )}

      {/* A/B Test Tab */}
      {tab === 'ab-test' && (
        <Card>
          <label style={s.label}>Existing Ad Copy *</label>
          <textarea value={abForm.existingCopy} onChange={e => setAbForm({ ...abForm, existingCopy: e.target.value })}
            style={{ ...s.select, height: 120, resize: 'vertical', fontFamily: 'inherit', marginBottom: 16 }} placeholder="তোমার বর্তমান ad copy paste করো..." />
          <Input label="Focus Areas (comma separated)" value={abForm.improvements}
            onChange={e => setAbForm({ ...abForm, improvements: e.target.value })} placeholder="hook, CTA, offer" />
          <Btn onClick={handleABTest} disabled={loading}>{loading ? '⏳...' : '🔀 Generate A/B Variants'}</Btn>
        </Card>
      )}

      {/* Analyze Tab */}
      {tab === 'analyze' && (
        <Card>
          <label style={s.label}>Ad Copy to Analyze *</label>
          <textarea value={analyzeForm.copy} onChange={e => setAnalyzeForm({ ...analyzeForm, copy: e.target.value })}
            style={{ ...s.select, height: 120, resize: 'vertical', fontFamily: 'inherit', marginBottom: 16 }} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Input label="CTR (%)" value={analyzeForm.ctr} onChange={e => setAnalyzeForm({ ...analyzeForm, ctr: e.target.value })} type="number" />
            <Input label="CPC (৳)" value={analyzeForm.cpc} onChange={e => setAnalyzeForm({ ...analyzeForm, cpc: e.target.value })} type="number" />
            <Input label="ROAS" value={analyzeForm.roas} onChange={e => setAnalyzeForm({ ...analyzeForm, roas: e.target.value })} type="number" />
          </div>
          <Btn onClick={handleAnalyze} disabled={loading}>{loading ? '⏳...' : '🔬 Analyze Copy'}</Btn>
        </Card>
      )}

      {loading && <Loading />}

      {/* Results — Generate */}
      {result?.type === 'generate' && result.data?.copies && (
        <div>
          <div style={{ fontWeight: 700, marginBottom: 16, fontSize: 16 }}>
            {result.data.copies.length}টি Copy Generated — Best: Variant {result.data.bestVariant}
          </div>
          {result.data.copies.map((copy, i) => (
            <div key={i} style={s.copy}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <span style={{ fontWeight: 700, color: '#a29bfe' }}>Variant {copy.variant}</span>
                <span style={{ ...s.tag, color: ctrColor[copy.estimatedCTR] || '#fff', background: 'rgba(0,0,0,0.3)' }}>
                  {copy.estimatedCTR} CTR
                </span>
                {copy.variant === result.data.bestVariant && (
                  <span style={{ ...s.tag, color: '#ffd200', background: 'rgba(255,210,0,0.1)' }}>⭐ Best</span>
                )}
                <span style={{ fontSize: 12, color: '#6b6b80', marginLeft: 'auto' }}>{copy.angle}</span>
              </div>
              <div style={{ background: '#16161f', borderRadius: 8, padding: 16, fontSize: 14, lineHeight: 1.7, whiteSpace: 'pre-wrap', marginBottom: 10 }}>
                {copy.fullCopy}
              </div>
              <div style={{ fontSize: 12, color: '#6b6b80' }}>Best for: {copy.bestFor}</div>
              <Btn color="#2a2a3a" onClick={() => navigator.clipboard.writeText(copy.fullCopy)} style={{ marginTop: 8, fontSize: 11, padding: '5px 10px' }}>
                📋 Copy
              </Btn>
            </div>
          ))}
          {result.data.generalTips?.length > 0 && (
            <Card>
              <div style={{ fontWeight: 700, marginBottom: 10 }}>💡 General Tips</div>
              {result.data.generalTips.map((tip, i) => (
                <div key={i} style={{ fontSize: 14, color: '#a29bfe', marginBottom: 6 }}>• {tip}</div>
              ))}
            </Card>
          )}
        </div>
      )}

      {/* Results — A/B Test */}
      {result?.type === 'abtest' && result.data?.variants && (
        <div>
          <div style={{ fontWeight: 700, marginBottom: 16, fontSize: 16 }}>A/B Test Variants</div>
          {result.data.variants.map((v, i) => (
            <div key={i} style={s.copy}>
              <div style={{ fontWeight: 700, color: '#a29bfe', marginBottom: 8 }}>Variant {v.variant}</div>
              <div style={{ fontSize: 13, color: '#6b6b80', marginBottom: 8 }}>Change: {v.change}</div>
              <div style={{ background: '#16161f', borderRadius: 8, padding: 16, fontSize: 14, lineHeight: 1.7, whiteSpace: 'pre-wrap', marginBottom: 8 }}>{v.copy}</div>
              <div style={{ fontSize: 12, color: '#55efc4' }}>💡 {v.hypothesis}</div>
            </div>
          ))}
          {result.data.testingRecommendation && (
            <Card><div style={{ fontSize: 14, color: '#ffd200' }}>📋 {result.data.testingRecommendation}</div></Card>
          )}
        </div>
      )}

      {/* Results — Analyze */}
      {result?.type === 'analyze' && result.data && (
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div style={{ fontSize: 48, fontWeight: 700, color: '#a29bfe' }}>{result.data.score}/10</div>
            <div>
              <div style={{ fontSize: 14, marginBottom: 4 }}>Hook: <span style={{ color: ctrColor[result.data.hookStrength] }}>{result.data.hookStrength}</span></div>
              <div style={{ fontSize: 14 }}>CTA: <span style={{ color: ctrColor[result.data.ctaStrength] }}>{result.data.ctaStrength}</span></div>
            </div>
            <div style={{ flex: 1, fontSize: 14, color: '#a29bfe', lineHeight: 1.6 }}>{result.data.summary}</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
            <div>
              <div style={{ fontWeight: 700, color: '#55efc4', marginBottom: 8 }}>✅ Strengths</div>
              {result.data.strengths?.map((s, i) => <div key={i} style={{ fontSize: 13, marginBottom: 4 }}>• {s}</div>)}
            </div>
            <div>
              <div style={{ fontWeight: 700, color: '#ff6584', marginBottom: 8 }}>❌ Weaknesses</div>
              {result.data.weaknesses?.map((s, i) => <div key={i} style={{ fontSize: 13, marginBottom: 4 }}>• {s}</div>)}
            </div>
            <div>
              <div style={{ fontWeight: 700, color: '#ffd200', marginBottom: 8 }}>💡 Improvements</div>
              {result.data.improvements?.map((s, i) => <div key={i} style={{ fontSize: 13, marginBottom: 4 }}>• {s}</div>)}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
